import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import Footer from '../components/Footer';
import { api } from '../services/authenticatedApi';
import { useJWTAuth } from '../contexts/useJWTAuth';
import { useSocket } from '../contexts/socketTypes';
import { logger } from '../utils/logger';
import { maskEmail } from '../utils/email';
import { API_CONFIG } from '../config/api.config';
import { VerifyEmailLeftPanel } from './VerifyEmailLeftPanel';
import { VerifyEmailStatusCard } from './VerifyEmailStatusCard';
import type { ResendStatus, VerifyEmailState } from '../types/verifyEmail';

interface VerifyCompletePayload {
  exchangeUrl?: string;
}

export const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const { user, signOut } = useJWTAuth();
  const { socket, isConnected } = useSocket();

  const [state, setState] = useState<VerifyEmailState>({
    status: token ? 'loading' : 'awaiting',
    message: token ? 'Verifying your email...' : 'We sent a verification link to your email',
  });

  const [resendStatus, setResendStatus] = useState<ResendStatus>('idle');
  const [resendError, setResendError] = useState<string>('');
  // Survives refresh in the same tab — populated at sign-up by JWTAuthContext.
  const [pendingSession, setPendingSession] = useState<{
    email: string;
    claim: string;
  } | null>(null);
  const redirectTimerRef = useRef<number | null>(null);

  // Prevent double-invocation in React StrictMode
  const verificationAttempted = useRef(false);

  // Hydrate the pending verification session from sessionStorage. Only relevant
  // for the awaiting flow (Browser A) — when we have a token, this page is
  // doing the verification itself.
  useEffect(() => {
    if (token) return;
    try {
      const raw = sessionStorage.getItem('verify_session');
      if (!raw) return;
      const parsed = JSON.parse(raw) as { email?: string; claim?: string };
      if (parsed.email && parsed.claim) {
        setPendingSession({ email: parsed.email, claim: parsed.claim });
      }
    } catch {
      // Malformed entry — ignore and fall through to the unbranded awaiting state.
    }
  }, [token]);

  // Redirect already verified users to dashboard
  useEffect(() => {
    if (user?.emailVerified && !token) {
      navigate('/dashboard', { replace: true });
    }
  }, [user?.emailVerified, token, navigate]);

  useEffect(() => {
    if (token && !verificationAttempted.current) {
      verificationAttempted.current = true;
      verifyEmail(token);
    }
    // verifyEmail is defined inline below; running once per token is intentional
  }, [token]);

  // Browser A: subscribe to the cross-browser verification hand-off. We
  // present our verify-pending claim; the server joins us to verify:{userId}
  // and emits 'verify:complete' once the link is clicked anywhere, with an
  // exchange URL we redeem to mint our session. The `cancelled` flag plus
  // the explicit `socket.off` make the listener idempotent — a verify event
  // that arrives twice (concurrent broadcast + post-join already-verified
  // catch-up) only triggers the redirect once.
  useEffect(() => {
    if (token || !pendingSession || state.status !== 'awaiting') return;
    if (!socket || !isConnected) return;

    let cancelled = false;
    const { claim } = pendingSession;

    socket.emit('join-verification', { claim }, (ack: { success?: boolean }) => {
      if (!ack?.success) {
        logger.debug('join-verification did not ack success', { ack });
      }
    });

    const handleComplete = (payload: VerifyCompletePayload) => {
      if (cancelled || !payload?.exchangeUrl) return;
      cancelled = true;
      socket.off('verify:complete', handleComplete);

      sessionStorage.removeItem('verify_session');

      setState({
        status: 'success',
        message: 'Signing you in...',
      });

      // Brief flash so the redirect doesn't feel like the page broke, then
      // hand off to the exchange endpoint which mints cookies and lands us
      // on the dashboard. The server emits a relative path; prefix with the
      // API origin so the browser hits the server, not the React frontend.
      redirectTimerRef.current = window.setTimeout(() => {
        const exchangePath = payload.exchangeUrl as string;
        const destination = exchangePath.startsWith('http')
          ? exchangePath
          : `${API_CONFIG.apiUrl}${exchangePath}`;
        window.location.assign(destination);
      }, 600);
    };

    socket.on('verify:complete', handleComplete);

    return () => {
      cancelled = true;
      socket.off('verify:complete', handleComplete);
    };
  }, [token, pendingSession, state.status, socket, isConnected]);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current !== null) {
        window.clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  const verifyEmail = async (verificationToken: string) => {
    try {
      setState({
        status: 'loading',
        message: 'Verifying your email address...',
      });

      await api.get(`/auth/verify-email/${verificationToken}`);

      // This browser doesn't get a session — verification is delivered to the
      // original sign-up tab over Socket.IO. Browser B's job is done.
      setState({
        status: 'success',
        message: 'You can close this tab now.',
      });
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { code?: string; error?: string } } };

      if (axiosError.response?.data?.code === 'TOKEN_EXPIRED') {
        setState({
          status: 'expired',
          message: 'Your verification link has expired. Please request a new one.',
        });
      } else if (!axiosError.response) {
        logger.error('Email verification error', { error });
        setState({
          status: 'error',
          message: 'Network error. Please check your connection and try again.',
        });
      } else {
        logger.error('Email verification error', { error });
        setState({
          status: 'error',
          message: axiosError.response.data?.error || 'Failed to verify email. Please try again.',
        });
      }
    }
  };

  const handleResendVerification = async () => {
    // Prefer the in-memory user, fall back to the persisted session for
    // post-refresh Browser A where the auth context is empty.
    const email = user?.email || pendingSession?.email;

    if (!email) {
      navigate('/signin');
      return;
    }

    setResendStatus('sending');
    setResendError('');

    try {
      await api.post('/auth/resend-verification', { email });

      setResendStatus('sent');
      if (state.status === 'error' || state.status === 'expired') {
        setState({
          status: 'awaiting',
          message: 'We sent a new verification link to your email',
        });
      }
    } catch (error: unknown) {
      setResendStatus('error');
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      setResendError(
        axiosError.response?.data?.error ||
          axiosError.message ||
          'Failed to send verification email',
      );
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const awaitingEmail = pendingSession?.email || user?.email;
  const successMode: 'signing-in' | 'handoff' | undefined =
    state.status === 'success' ? (token ? 'handoff' : 'signing-in') : undefined;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar showAuthButton={false} />

      <main className="flex-1 px-6 lg:px-8 py-12 pt-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="hidden lg:block space-y-8">
              <VerifyEmailLeftPanel status={state.status} />
            </div>

            <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
              <VerifyEmailStatusCard
                state={state}
                resendStatus={resendStatus}
                resendError={resendError}
                userEmail={awaitingEmail ? maskEmail(awaitingEmail) : undefined}
                hasUser={!!user}
                onResend={handleResendVerification}
                onSignOut={handleSignOut}
                successMode={successMode}
              />
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default VerifyEmailPage;
